// /app/frontend/src/hooks/useTypingIndicator.js
import { useEffect, useRef, useState } from "react";

/**
 * useTypingIndicator
 * 
 * Manages typing indicator state for a chat conversation.
 * Shows when the OTHER person is typing (never your own typing).
 * Auto-clears after 5 seconds of no activity.
 * 
 * @param {Object} params
 * @param {Object} params.ably - Ably Realtime client instance
 * @param {string} params.conversationId - Unique conversation/match ID
 * @param {string} params.currentUserId - Current user's ID
 * @returns {Object} { isPartnerTyping: boolean, sendTyping: function }
 */
export function useTypingIndicator({ ably, conversationId, currentUserId }) {
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const lastSentTypingRef = useRef(false); // track last isTyping state we sent

  useEffect(() => {
    if (!ably || !conversationId || !currentUserId) return;

    const channelName = `chat:match:${conversationId}`;
    const channel = ably.channels.get(channelName);
    
    // Add safe error handlers to prevent crashes
    const safeErrorHandler = (err) => {
      console.error('[TypingIndicator] Channel error:', err);
      // DO NOT throw
    };
    const safeDetachedHandler = (stateChange) => {
      console.warn('[TypingIndicator] Channel detached:', stateChange?.reason || '');
      // DO NOT throw - normal lifecycle
    };
    
    channel.off('error', safeErrorHandler);
    channel.off('detached', safeDetachedHandler);
    channel.on('error', safeErrorHandler);
    channel.on('detached', safeDetachedHandler);

    const handler = (msg) => {
      const data = msg.data || {};
      
      // Ignore our own typing events
      if (!data || data.fromUserId === currentUserId) return;

      if (data.type === "typing") {
        // This is ALWAYS about the other person, never you
        if (data.isTyping) {
          setIsPartnerTyping(true);

          // Auto-clear after 5 seconds in case stop event is missed
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
          }
          typingTimeoutRef.current = setTimeout(() => {
            setIsPartnerTyping(false);
          }, 5000);
        } else {
          setIsPartnerTyping(false);
          if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
          }
        }
      }
    };

    channel.subscribe("typing", handler);

    return () => {
      channel.unsubscribe("typing", handler);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      setIsPartnerTyping(false);
    };
  }, [ably, conversationId, currentUserId]);

  /**
   * Send typing status to partner
   * @param {boolean} isTyping - true when typing, false when stopped
   */
  const sendTyping = (isTyping) => {
    if (!ably || !conversationId || !currentUserId) return;

    // Avoid spamming identical states
    if (lastSentTypingRef.current === isTyping) return;
    lastSentTypingRef.current = isTyping;

    const channelName = `chat:match:${conversationId}`;
    const channel = ably.channels.get(channelName);
    
    // Add safe error handlers
    const safeErrorHandler = (err) => {
      console.error('[TypingIndicator-Send] Channel error:', err);
    };
    const safeDetachedHandler = (stateChange) => {
      console.warn('[TypingIndicator-Send] Channel detached:', stateChange?.reason || '');
    };
    
    channel.off('error', safeErrorHandler);
    channel.off('detached', safeDetachedHandler);
    channel.on('error', safeErrorHandler);
    channel.on('detached', safeDetachedHandler);
    
    channel.publish("typing", {
      type: "typing",
      fromUserId: currentUserId,
      conversationId,
      isTyping,
    });
  };

  return {
    isPartnerTyping,
    sendTyping,
  };
}
